/**
 * The Acquisition tab's rules (docs/plans/2026-10-11_acquisition.md, P2d), pure so they are tested apart from the
 * pages: the study's chip, the upload's checks, the review table's filters and bulk decisions, what a row reads as, the
 * row editor's form, its checks and the decision it saves, the parameters a row may be, the questions for the plant,
 * what an apply is about to write and wrote, and the words for who planned a study (P3: the rules or the brain, the
 * brain's notes, and why a brain plan was not used).
 *
 * The editor's checks mirror the server's (internal/acquisition/edits.go, checkTarget and parseRate), so a person
 * hears what is wrong before the save, in plain words; the server still decides. A bulk decision goes to the server in
 * batches of at most its limit (chunkDecisions, putInChunks), since it refuses a longer one whole.
 */
import type {
  AcquisitionApplyResult,
  AcquisitionBrainLoad,
  AcquisitionBrainNote,
  AcquisitionBrainNotes,
  AcquisitionBrainWindow,
  AcquisitionDecisionIn,
  AcquisitionEdit,
  AcquisitionGap,
  AcquisitionPlanBy,
  AcquisitionPoint,
  AcquisitionProfiles,
  AcquisitionRate,
  AcquisitionReviewRow,
  AcquisitionSourceKind,
  AcquisitionStudyStatus,
  AcquisitionStudySummary,
  AcquisitionTarget,
  AcquisitionUpload,
  AcquisitionUploadKind,
} from "@/lib/api/acquisition";
import type { Station, StationCoverageRow } from "@/lib/api/stations";

/** The Acquisition tab of Data sources, and a study's own page under it. */
export const ACQUISITION_ROUTE = "/app/twin/data-sources/acquisition";
export const studyHref = (studyId: string): string => `${ACQUISITION_ROUTE}/${encodeURIComponent(studyId)}`;

// ── The study's chip ─────────────────────────────────────────────────────────

export type ChipTone = "secondary" | "info" | "warning" | "success" | "danger";

export interface StudyChip {
  label: string;
  tone: ChipTone;
  /** A failed study's reason, in the worker's words. */
  reason?: string;
}

const CHIP: Record<AcquisitionStudyStatus, Omit<StudyChip, "reason">> = {
  queued: { label: "Studying", tone: "info" },
  profiling: { label: "Studying", tone: "info" },
  proposed: { label: "Ready for review", tone: "warning" },
  applied: { label: "Applied", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
};

/** A study's status as its chip: Studying, Ready for review, Applied, or Failed with its reason. */
export function studyChipOf(study: Pick<AcquisitionStudySummary, "status" | "error"> | undefined): StudyChip {
  if (!study) return { label: "Not studied", tone: "secondary" };
  const chip = CHIP[study.status] ?? { label: study.status, tone: "secondary" as const };
  if (study.status !== "failed") return chip;
  return { ...chip, reason: study.error?.trim() || "The study could not read the file." };
}

export const SOURCE_KIND_WORDS: Record<AcquisitionSourceKind, string> = {
  tag_list: "Tag list",
  data_export: "Data export",
  probe_window: "Probe window",
  agent_kind: "Plant agent file",
};

// ── The upload ───────────────────────────────────────────────────────────────

/** The most a source's file may be: the import intake's cap. */
export const UPLOAD_MAX_BYTES = 64 * 1024 * 1024;
/** The most a source's name may be. */
export const NAME_MAX = 300;

export interface UploadDraft {
  file: Pick<File, "name" | "size"> | null;
  kind: AcquisitionUploadKind | "";
  /** A station's id, or "" for none. */
  station: string;
  name: string;
}

export const EMPTY_UPLOAD: UploadDraft = { file: null, kind: "", station: "", name: "" };

/** What stops an upload, in plain words; none when it may go. */
export function uploadProblems(d: UploadDraft): string[] {
  const out: string[] = [];
  if (!d.file) out.push("Choose a file.");
  else if (d.file.size === 0) out.push(`${d.file.name} is empty.`);
  else if (d.file.size > UPLOAD_MAX_BYTES) out.push(`${d.file.name} is over 64 MB, the most a source may be.`);
  if (d.kind === "") out.push("Say whether the file is a tag list or a data export.");
  if (d.name.trim().length > NAME_MAX) out.push(`The name is at most ${NAME_MAX} characters.`);
  return out;
}

/** The upload a draft makes; the caller checks uploadProblems first. */
export function uploadOf(d: UploadDraft & { file: File }): AcquisitionUpload {
  return {
    file: d.file,
    kind: d.kind as AcquisitionUploadKind,
    ...(d.station ? { station_hint: d.station } : {}),
    ...(d.name.trim() ? { name: d.name.trim() } : {}),
  };
}

// ── The review table: filters and bulk decisions ─────────────────────────────

export type RowFilter = "all" | "undecided" | "low" | "problems" | "unmapped" | "brain";

/**
 * Below this, a mapped row's proposal rests on weak evidence: a profile signal's words in its comment, or a
 * parameter's label (the composer's scores, internal/acquisition/plan.go). A row is mapped from 0.6.
 */
export const LOW_CONFIDENCE = 0.75;

export const FILTERS: readonly { id: RowFilter; label: string; title: string }[] = [
  { id: "all", label: "All", title: "Every point of the file" },
  { id: "undecided", label: "Undecided", title: "Rows nobody has accepted, edited or rejected yet" },
  { id: "low", label: "Low confidence", title: "Mapped rows whose evidence is weak: worth a look before accepting" },
  { id: "problems", label: "Problems", title: "Rows with something to fix on site" },
  { id: "unmapped", label: "Unmapped", title: "Rows the platform could not say what they are" },
  { id: "brain", label: "Changed by the brain", title: "Rows the brain wrote over the rules' plan" },
];

/** The filters a study offers: the brain's own only where the brain wrote its plan. */
export function filtersOf(brainPlan: boolean): readonly (typeof FILTERS)[number][] {
  return brainPlan ? FILTERS : FILTERS.filter((f) => f.id !== "brain");
}

export function rowMatches(row: AcquisitionReviewRow, f: RowFilter): boolean {
  switch (f) {
    case "undecided":
      return !row.decision;
    case "low":
      return row.mapped && row.confidence < LOW_CONFIDENCE;
    case "problems":
      return row.problems.length > 0;
    case "unmapped":
      return !row.mapped;
    case "brain":
      return row.by_brain === true;
    default:
      return true;
  }
}

export function filterRows(rows: readonly AcquisitionReviewRow[], f: RowFilter): AcquisitionReviewRow[] {
  return rows.filter((r) => rowMatches(r, f));
}

/** The row of one point (a link in the brain's notes opens it), whatever the filter and the search say. */
export function rowsOfPoint(rows: readonly AcquisitionReviewRow[], key: string): AcquisitionReviewRow[] {
  return rows.filter((r) => r.point_key === key);
}

export function filterCounts(rows: readonly AcquisitionReviewRow[]): Record<RowFilter, number> {
  const out = { all: 0, undecided: 0, low: 0, problems: 0, unmapped: 0, brain: 0 };
  for (const r of rows) for (const f of FILTERS) if (rowMatches(r, f.id)) out[f.id]++;
  return out;
}

/**
 * Rows whose name, path or address contain the text, whatever its case; every row for text that is blank. The path and
 * the address are the point's (the file's), the name the row's.
 */
export function filterByText(
  rows: readonly AcquisitionReviewRow[],
  points: ReadonlyMap<string, Pick<AcquisitionPoint, "path" | "address">>,
  text: string,
): AcquisitionReviewRow[] {
  const needle = text.trim().toLowerCase();
  if (needle === "") return [...rows];
  return rows.filter((r) => {
    const p = points.get(r.point_key);
    return [r.name, p?.path, p?.address].some((s) => !!s && s.toLowerCase().includes(needle));
  });
}

/** The decisions a bulk Accept or Reject makes of the selected rows, in the table's order. */
export function bulkDecisions(
  rows: readonly AcquisitionReviewRow[],
  selected: ReadonlySet<string>,
  decision: "accept" | "reject",
): AcquisitionDecisionIn[] {
  return rows.filter((r) => selected.has(r.point_key)).map((r) => ({ point_key: r.point_key, decision }));
}

/** The selection with these keys added (`on`) or taken out; the selection given is left as it is. */
export function selectionWith(selected: ReadonlySet<string>, keys: readonly string[], on: boolean): Set<string> {
  const next = new Set(selected);
  for (const k of keys) {
    if (on) next.add(k);
    else next.delete(k);
  }
  return next;
}

/**
 * The selection after a save that stopped part way: the first `saved` decisions are stored (the batches go one after
 * the other, in order), so their rows leave the selection and the rest stay selected, to be sent again.
 */
export function selectionAfterPartial(
  selected: ReadonlySet<string>,
  decisions: readonly Pick<AcquisitionDecisionIn, "point_key">[],
  saved: number,
): Set<string> {
  return selectionWith(
    selected,
    decisions.slice(0, Math.max(0, saved)).map((d) => d.point_key),
    false,
  );
}

/** The most decisions the server takes in one PUT (internal/acquisition/edits.go, maxDecisions); it refuses more whole. */
export const MAX_DECISIONS = 2000;

/** A list cut into batches of at most `size`, in order; none for an empty list. */
export function chunkDecisions<T>(list: readonly T[], size = MAX_DECISIONS): T[][] {
  const step = Math.max(1, Math.floor(size));
  const out: T[][] = [];
  for (let i = 0; i < list.length; i += step) out.push(list.slice(i, i + step));
  return out;
}

/** A save that stopped part way: `saved` of `total` decisions are stored, and `reason` is what stopped the rest. */
export class PartialSave extends Error {
  readonly saved: number;
  readonly total: number;
  readonly reason: unknown;
  constructor(saved: number, total: number, reason: unknown) {
    super(`saved ${saved} of ${total} decisions`);
    this.name = "PartialSave";
    this.saved = saved;
    this.total = total;
    this.reason = reason;
  }
}

/**
 * Puts a long list of decisions in batches of at most `size`, one after the other (never together, so the first
 * refusal stops the rest), and returns how many are saved. A refusal of the first batch is thrown as it came, since
 * nothing was saved; a later one is thrown as a PartialSave that says how far it got.
 */
export async function putInChunks<T>(
  list: readonly T[],
  put: (chunk: T[]) => Promise<unknown>,
  size = MAX_DECISIONS,
): Promise<number> {
  let saved = 0;
  for (const chunk of chunkDecisions(list, size)) {
    try {
      await put(chunk);
    } catch (err) {
      if (saved === 0) throw err;
      throw new PartialSave(saved, list.length, err);
    }
    saved += chunk.length;
  }
  return saved;
}

export const DECISION_CHIP: Record<"undecided" | NonNullable<AcquisitionReviewRow["decision"]>, StudyChip> = {
  undecided: { label: "Undecided", tone: "secondary" },
  accept: { label: "Accepted", tone: "success" },
  edit: { label: "Edited", tone: "info" },
  reject: { label: "Rejected", tone: "danger" },
};

export function decisionChipOf(row: Pick<AcquisitionReviewRow, "decision">): StudyChip {
  return DECISION_CHIP[row.decision ?? "undecided"];
}

/** What a row reads as now: what its decision makes of it, else its proposal (an undecided or rejected row). */
export function shownTarget(row: AcquisitionReviewRow): AcquisitionTarget {
  return row.target ?? row.proposal;
}

// ── Words for a row ──────────────────────────────────────────────────────────

/** A span of seconds in plain words: 30 s, 5 min, 1 h, 1 day. */
export function secondsWords(s: number): string {
  if (s >= 86400 && s % 86400 === 0) return s === 86400 ? "1 day" : `${s / 86400} days`;
  if (s >= 3600 && s % 3600 === 0) return `${s / 3600} h`;
  if (s >= 60 && s % 60 === 0) return `${s / 60} min`;
  return `${s} s`;
}

/** How a point is read, in words: Per cycle, On change, or Every 5 s running, 1 min idle. */
export function rateWords(rate: AcquisitionRate | undefined): string {
  if (!rate) return "Not set";
  if (rate.per_cycle) return "Per cycle";
  if (rate.on_event) return "On change";
  if (rate.running) {
    const every = `Every ${secondsWords(rate.running)}`;
    return rate.idle && rate.idle !== rate.running ? `${every} running, ${secondsWords(rate.idle)} idle` : every;
  }
  return "Not set";
}

export function heartbeatWords(s: number | undefined): string {
  return s ? secondsWords(s) : "None";
}

export function confidenceWords(c: number): string {
  return `${Math.round(Math.max(0, Math.min(1, c)) * 100)}%`;
}

/** A point's place in the controller: its path and its address, as far as the file gives them. */
export function pointWhere(p: Pick<AcquisitionPoint, "path" | "address"> | undefined): string {
  return [p?.path, p?.address].filter((s) => !!s && s.trim() !== "").join(", ");
}

// ── The pack's parameters, the study's counters, the profiles' words ────────

export interface ParamOption {
  /** The pack parameter's canonical name, as a decision names it. */
  param: string;
  label: string;
  unit?: string;
  /** The classes whose profiles list it. */
  classes: string[];
}

/**
 * Every pack parameter a row may be, from the profiles' signals (each signal that is a pack parameter) and the rows'
 * own candidates, once each, by label. There is no route for the pack's parameters alone; the profiles hold every
 * parameter a plant's equipment records, and a row's candidates hold the rest it matched.
 */
export function paramOptions(
  profiles: AcquisitionProfiles | undefined,
  rows: readonly AcquisitionReviewRow[] = [],
): ParamOption[] {
  const by = new Map<string, ParamOption>();
  for (const c of profiles?.classes ?? []) {
    for (const s of c.signals) {
      if (!s.param) continue;
      const o = by.get(s.param);
      if (o) {
        if (!o.classes.includes(c.label)) o.classes.push(c.label);
        o.unit ??= s.unit;
      } else {
        by.set(s.param, { param: s.param, label: s.label, unit: s.unit, classes: [c.label] });
      }
    }
  }
  for (const r of rows) {
    for (const c of r.candidates) {
      if (c.param && !by.has(c.param)) by.set(c.param, { param: c.param, label: c.label, classes: [] });
    }
  }
  return [...by.values()].sort((a, b) => a.label.localeCompare(b.label) || a.param.localeCompare(b.param));
}

/** A parameter in words: its label, else its name. */
export function paramWords(param: string | undefined, options: readonly ParamOption[]): string {
  if (!param) return "";
  return options.find((o) => o.param === param)?.label ?? param;
}

/** A profile class in words: its label, else its id. */
export function classWords(id: string | undefined, profiles: AcquisitionProfiles | undefined): string {
  if (!id) return "";
  return profiles?.classes.find((c) => c.id === id)?.label ?? id;
}

/**
 * The points of a study a per-cycle row may be triggered by: a point whose facts say it counts (counter or
 * cycle_counter), or whose proposal is a profile's counter signal, as the server checks a trigger.
 */
export function counterKeys(
  points: readonly AcquisitionPoint[],
  rows: readonly AcquisitionReviewRow[],
  profiles: AcquisitionProfiles | undefined,
): string[] {
  const out = new Set<string>();
  for (const p of points) if (p.facts?.counter === true || p.facts?.cycle_counter === true) out.add(p.key);
  for (const r of rows) {
    const c = profiles?.classes.find((k) => k.id === r.proposal.class);
    if (c?.signals.find((s) => s.id === r.proposal.signal)?.kind === "counter") out.add(r.point_key);
  }
  return [...out];
}

/**
 * The trigger points the editor lists besides the study's counters: the row's proposal's own trigger and the one the
 * form holds now (each unless it is the row's own point, or a counter already listed), so the proposal's trigger can be
 * chosen again after the form changes it.
 */
export function extraTriggerKeys(
  formTrigger: string,
  proposalTrigger: string | undefined,
  pointKey: string,
  counters: readonly string[],
): string[] {
  const out: string[] = [];
  for (const k of [proposalTrigger, formTrigger]) {
    if (k && k !== pointKey && !counters.includes(k) && !out.includes(k)) out.push(k);
  }
  return out;
}

export interface PlantQuestion {
  classLabel: string;
  question: string;
}

/** The questions for the plant: each matched class's own, in the profiles' order. */
export function plantQuestions(profiles: AcquisitionProfiles | undefined, classes: readonly string[]): PlantQuestion[] {
  return (profiles?.classes ?? [])
    .filter((c) => classes.includes(c.id))
    .flatMap((c) => c.questions.map((question) => ({ classLabel: c.label, question })));
}

/** A gap's key: its class and its signal (the class's id is a profile's, never an entity's kind). */
export function gapKeyOf(g: Pick<AcquisitionGap, "class" | "signal">): string {
  return [g.class, g.signal].join(".");
}

export interface PointProblem {
  name: string;
  problem: string;
}

export function pointProblems(rows: readonly AcquisitionReviewRow[]): PointProblem[] {
  return rows.flatMap((r) => r.problems.map((problem) => ({ name: r.name, problem })));
}

/** The questions and problems as a plain numbered list, for the plant (the Copy button). */
export function questionsText(questions: readonly PlantQuestion[], problems: readonly PointProblem[]): string {
  const parts: string[] = [];
  if (questions.length > 0) {
    parts.push(
      ["Questions for the plant", ...questions.map((q, i) => `${i + 1}. ${q.question} (${q.classLabel})`)].join("\n"),
    );
  }
  if (problems.length > 0) {
    parts.push(["Problems to fix on site", ...problems.map((p, i) => `${i + 1}. ${p.name}: ${p.problem}`)].join("\n"));
  }
  return parts.join("\n\n");
}

// ── The row editor ───────────────────────────────────────────────────────────

/** Read per cycle (when its trigger steps), on change, or every so many seconds. */
export type RateMode = "per_cycle" | "on_event" | "every";

/** The editor's fields as the person types them: text, so a half typed number is kept as it is. */
export interface EditorForm {
  /** A station's id, or "" for none. */
  station_id: string;
  entity_id: string;
  param: string;
  unit: string;
  scale: string;
  keep: boolean;
  rateMode: RateMode;
  running: string;
  idle: string;
  deadband: string;
  /** Seconds, or "" for none. */
  heartbeat: string;
  /** A counter's point key, or "" for none. */
  trigger: string;
}

const numText = (n: number | undefined): string => (n === undefined || n === null ? "" : String(n));

/** The editor's fields for a row: what the row reads as now (its decision's target, else its proposal). */
export function editorFormOf(row: AcquisitionReviewRow): EditorForm {
  const t = shownTarget(row);
  const r = t.rate ?? {};
  return {
    station_id: t.station_id ?? "",
    entity_id: t.entity_id ?? "",
    param: t.param ?? "",
    unit: t.unit ?? "",
    scale: numText(t.scale),
    keep: t.keep,
    rateMode: r.per_cycle ? "per_cycle" : r.on_event ? "on_event" : "every",
    running: numText(r.running || undefined),
    idle: numText(r.idle || undefined),
    deadband: numText(t.deadband),
    heartbeat: t.heartbeat ? String(t.heartbeat) : "",
    trigger: t.trigger ?? "",
  };
}

/**
 * The form after a field changes, keeping what a target must read: a per-cycle point has no deadband and no heartbeat
 * (its record is read whole at each cycle), and only a per-cycle point has a trigger.
 */
export function editorWith(form: EditorForm, patch: Partial<EditorForm>): EditorForm {
  const next = { ...form, ...patch };
  if (next.rateMode === "per_cycle") return { ...next, deadband: "", heartbeat: "" };
  return { ...next, trigger: "" };
}

const RATE_MAX = 86400;
const HEARTBEAT_MIN = 60;
const HEARTBEAT_MAX = 604800;
const UNIT_MAX = 40;
const ENTITY_MAX = 200;

/** A number typed in a field: undefined for an empty field, NaN for text that is no number. */
function parseNum(s: string): number | undefined {
  const t = s.trim();
  if (t === "") return undefined;
  const n = Number(t);
  return Number.isFinite(n) ? n : Number.NaN;
}

/** The rate the form says. */
export function rateOfForm(form: EditorForm): AcquisitionRate {
  if (form.rateMode === "per_cycle") return { per_cycle: true };
  if (form.rateMode === "on_event") return { on_event: true };
  const running = parseNum(form.running);
  const idle = parseNum(form.idle);
  return { ...(running !== undefined ? { running } : {}), ...(idle !== undefined ? { idle } : {}) };
}

/**
 * What stops the editor's save, in plain words; none when the row reads. `counters` are the study's counter points (a
 * trigger must be one, never the row's own point). The proposal's own trigger is left unchecked: the web never sends a
 * trigger that is the proposal's, and the server checks only a trigger an edit sets.
 */
export function editorProblems(
  form: EditorForm,
  pointKey: string,
  counters: readonly string[],
  proposalTrigger?: string,
): string[] {
  const out: string[] = [];
  const whole = (n: number | undefined) => n !== undefined && Number.isInteger(n);
  const scale = parseNum(form.scale);
  if (scale !== undefined && (Number.isNaN(scale) || scale === 0)) out.push("The scale is a number other than zero.");
  if (form.unit.trim().length > UNIT_MAX) out.push(`The unit is at most ${UNIT_MAX} characters.`);
  if (form.entity_id.trim().length > ENTITY_MAX) out.push("The equipment is too long a name.");
  let slowest = 0;
  if (form.rateMode === "every") {
    const running = parseNum(form.running);
    const idle = parseNum(form.idle);
    if (!whole(running) || (running as number) < 1 || (running as number) > RATE_MAX) {
      out.push(`Read it every 1 to ${RATE_MAX.toLocaleString("en-US")} seconds while running.`);
    } else {
      slowest = running as number;
    }
    if (idle !== undefined) {
      if (!whole(idle) || idle < 1 || idle > RATE_MAX) {
        out.push(`The idle rate is 1 to ${RATE_MAX.toLocaleString("en-US")} seconds.`);
      } else if (whole(running) && idle < (running as number)) {
        out.push("Idle reads no faster than running.");
      } else {
        slowest = Math.max(slowest, idle);
      }
    }
  }
  const deadband = parseNum(form.deadband);
  if (deadband !== undefined && (Number.isNaN(deadband) || deadband < 0)) out.push("The deadband is zero or more.");
  const heartbeat = parseNum(form.heartbeat);
  if (heartbeat !== undefined && heartbeat !== 0) {
    if (!whole(heartbeat) || heartbeat < HEARTBEAT_MIN || heartbeat > HEARTBEAT_MAX) {
      out.push(`The heartbeat is ${HEARTBEAT_MIN} to ${HEARTBEAT_MAX.toLocaleString("en-US")} seconds, or none.`);
    } else if (heartbeat < slowest) {
      out.push(`The heartbeat is never shorter than the rate, ${secondsWords(slowest)}.`);
    }
  }
  if (form.rateMode === "per_cycle") {
    if (deadband !== undefined || (heartbeat !== undefined && heartbeat !== 0)) {
      out.push("A per cycle point is read whole at each cycle: no deadband and no heartbeat.");
    }
    if (form.trigger === pointKey) out.push("A point is not its own trigger.");
    else if (form.trigger && form.trigger !== proposalTrigger && !counters.includes(form.trigger)) {
      out.push("The trigger is a counter of this file.");
    }
  } else if (form.trigger) {
    out.push("Only a per cycle point has a trigger.");
  }
  return out;
}

const same = (a: number | undefined, b: number | undefined) => (a ?? undefined) === (b ?? undefined);
const sameRate = (a: AcquisitionRate, b: AcquisitionRate) =>
  !!a.per_cycle === !!b.per_cycle && !!a.on_event === !!b.on_event && (a.running || 0) === (b.running || 0) &&
  (a.idle || 0) === (b.idle || 0);

/**
 * The decision the editor saves: an edit of every field the form sets otherwise than the proposal (a cleared field
 * null), since the server applies an edit to the proposal, never to an earlier edit; an accept when the form is the
 * proposal again. The caller checks editorProblems first.
 */
export function decisionOfEditor(row: AcquisitionReviewRow, form: EditorForm): AcquisitionDecisionIn {
  const p = row.proposal;
  const edited: AcquisitionEdit = {};
  const text = (s: string) => s.trim() || undefined;
  const station = text(form.station_id);
  if (station !== p.station_id) edited.station_id = station ?? null;
  const entity = text(form.entity_id);
  if (entity !== p.entity_id) edited.entity_id = entity ?? null;
  const param = text(form.param);
  if (param !== p.param) edited.param = param ?? null;
  const unit = text(form.unit);
  if (unit !== p.unit) edited.unit = unit ?? null;
  const scale = parseNum(form.scale);
  if (!same(scale, p.scale)) edited.scale = scale ?? null;
  if (form.keep !== p.keep) edited.keep = form.keep;
  const rate = rateOfForm(form);
  if (!sameRate(rate, p.rate ?? {})) edited.rate = rate;
  const deadband = parseNum(form.deadband);
  if (!same(deadband, p.deadband)) edited.deadband = deadband ?? null;
  const heartbeat = parseNum(form.heartbeat) ?? 0;
  if (heartbeat !== (p.heartbeat ?? 0)) edited.heartbeat = heartbeat;
  const trigger = text(form.trigger);
  if (trigger !== p.trigger) edited.trigger = trigger ?? null;
  if (Object.keys(edited).length === 0) return { point_key: row.point_key, decision: "accept" };
  return { point_key: row.point_key, decision: "edit", edited };
}

// ── Apply ────────────────────────────────────────────────────────────────────

export interface ApplyPreview {
  accepted: number;
  edited: number;
  rejected: number;
  undecided: number;
  /** Accepted or edited rows that keep the point and name a parameter: what binds in the vocabulary. */
  binding: number;
}

/** What an apply is about to write, from the rows' decisions. */
export function applyPreviewOf(rows: readonly AcquisitionReviewRow[]): ApplyPreview {
  const out: ApplyPreview = { accepted: 0, edited: 0, rejected: 0, undecided: 0, binding: 0 };
  for (const r of rows) {
    if (r.decision === "accept") out.accepted++;
    else if (r.decision === "edit") out.edited++;
    else if (r.decision === "reject") out.rejected++;
    else out.undecided++;
    if ((r.decision === "accept" || r.decision === "edit") && r.target?.keep && r.target.param) out.binding++;
  }
  return out;
}

/** The rows an apply skipped, by why, in words; only the reasons with a row. */
export function skippedLines(result: AcquisitionApplyResult): { label: string; count: number }[] {
  const s = result.skipped;
  return [
    { label: "Undecided", count: s.undecided },
    { label: "Rejected", count: s.rejected },
    { label: "Not kept", count: s.not_kept },
    { label: "No parameter", count: s.no_param },
    { label: "Two rows bound one name to two parameters", count: s.conflict },
    { label: "Refused", count: s.refused },
  ].filter((l) => l.count > 0);
}

/** Every skipped row, whatever the reason, a person's own vocabulary decisions apart. */
export function skippedTotal(result: AcquisitionApplyResult): number {
  return skippedLines(result).reduce((n, l) => n + l.count, 0);
}

/** One plural noun after its count: 1 row, 2 rows. */
export function count(n: number, one: string, many = `${one}s`): string {
  return `${n.toLocaleString("en-US")} ${n === 1 ? one : many}`;
}

// ── Words for the rows ───────────────────────────────────────────────────────

/** Names for the ids a row carries: stations, equipment, pack parameters, profile classes and the study's points. */
export interface ReviewWords {
  station: (id: string | undefined) => string;
  entity: (id: string | undefined) => string;
  param: (param: string | undefined) => string;
  klass: (id: string | undefined) => string;
  /** A profile signal's label by its class and its id; empty where the profiles do not list it. */
  signal: (klass: string | undefined, id: string | undefined) => string;
  point: (key: string | undefined) => string;
}

export function reviewWordsOf(a: {
  stations: readonly Pick<Station, "id" | "name">[];
  equipment: readonly Pick<StationCoverageRow, "entity_id" | "name">[];
  params: readonly ParamOption[];
  profiles: AcquisitionProfiles | undefined;
  points: readonly AcquisitionPoint[];
}): ReviewWords {
  const stations = new Map(a.stations.map((s) => [s.id, s.name] as const));
  const equipment = new Map(a.equipment.map((e) => [e.entity_id, e.name] as const));
  const points = new Map(a.points.map((p) => [p.key, p.name] as const));
  return {
    station: (id) => (id ? stations.get(id) ?? "a station no longer listed" : ""),
    entity: (id) => (id ? equipment.get(id) ?? id : ""),
    param: (p) => paramWords(p, a.params),
    klass: (id) => classWords(id, a.profiles),
    signal: (klass, id) =>
      klass && id ? a.profiles?.classes.find((c) => c.id === klass)?.signals.find((x) => x.id === id)?.label ?? "" : "",
    point: (key) => (key ? points.get(key) ?? key : ""),
  };
}

/**
 * What a row goes to when it names no pack parameter (a machine signal: a cycle counter, a weight, an id, a state): the
 * proposed signal's label from the profiles, else the label of the candidate that proposed it (the one for the same
 * class and signal, else the first), else the signal's id; empty for a row proposed as no signal.
 */
export function signalWordsOf(row: AcquisitionReviewRow, words: Pick<ReviewWords, "signal">): string {
  const t = shownTarget(row);
  if (!t.signal) return "";
  const candidate = row.candidates.find((c) => c.class === t.class && c.signal === t.signal) ?? row.candidates[0];
  return words.signal(t.class, t.signal) || candidate?.label || t.signal;
}

// ── Who planned a study, and the brain's notes ───────────────────────────────

/**
 * Who wrote a study's plan, in words: "Planned by the rules", or "Planned by the brain (model X), 3 rows changed from
 * the rules". Nothing before a plan is written.
 */
export function planByWords(
  planBy: AcquisitionPlanBy | undefined,
  brain?: Pick<AcquisitionBrainNotes, "model" | "rows_overridden">,
): string {
  if (planBy === "rules") return "Planned by the rules";
  if (planBy !== "brain") return "";
  const model = brain?.model?.trim();
  const who = model ? `Planned by the brain (model ${model})` : "Planned by the brain";
  return brain ? `${who}, ${count(brain.rows_overridden, "row")} changed from the rules` : who;
}

/** The heading of the notice for a plan the brain wrote and the server did not use. */
export const PLAN_NOT_USED = "The brain's plan was not used";

/**
 * Why the brain's plan was not used, as the server gives it, as a sentence (a capital first letter and a full stop):
 * the notice's words under PLAN_NOT_USED. The server's words already say the rules' plan stands where they need to,
 * so nothing is added to them. Nothing where there is no reason.
 */
export function planErrorWords(planError: string | undefined): string {
  const t = (planError ?? "").trim();
  if (!t.replace(/[.!?\s]/g, "")) return "";
  const head = t[0].toUpperCase() + t.slice(1);
  return /[.!?]$/.test(head) ? head : `${head}.`;
}

export interface PlanChip {
  /** What the chip reads. */
  label: "Brain" | "Rules";
  /** What it means, for a screen reader to read before the label. */
  prefix: string;
}

/** The chip of the sources list: who wrote the latest study's plan; none until the study is proposed or applied. */
export function planChipOf(study: Pick<AcquisitionStudySummary, "status" | "plan_by"> | undefined): PlanChip | undefined {
  if (!study || (study.status !== "proposed" && study.status !== "applied")) return undefined;
  if (study.plan_by === "brain") return { label: "Brain", prefix: "Planned by " };
  if (study.plan_by === "rules") return { label: "Rules", prefix: "Planned by " };
  return undefined;
}

/** The brain's notes with every list present: a list the server sent as null reads as empty. */
export function brainNotesOf(notes: AcquisitionBrainNotes | null | undefined): AcquisitionBrainNotes | undefined {
  if (!notes) return undefined;
  const withPoints = (list: AcquisitionBrainNote[] | null | undefined): AcquisitionBrainNote[] =>
    (list ?? []).map((n) => ({ ...n, points: n.points ?? [] }));
  return {
    ...notes,
    model: notes.model ?? "",
    summary: (notes.summary ?? "").trim(),
    site_problems: withPoints(notes.site_problems),
    questions: withPoints(notes.questions),
    windows: notes.windows ?? [],
  };
}

const PLAIN_NUMBER = new Intl.NumberFormat("en-US", { maximumFractionDigits: 6, useGrouping: false });

/** A process value as the server gives it: whole or with its decimals, never grouped. */
export function plainNumber(n: number): string {
  return PLAIN_NUMBER.format(n);
}

/** A unit that is only a dash (a source writes one for a number with none) says nothing, so it is not shown. */
const DASH_ONLY = new RegExp(`^[-${String.fromCharCode(0x2010, 0x2011, 0x2012, 0x2013, 0x2014, 0x2015, 0x2212)}]+$`);

/** A starting window in words: 1380 to 1450 °C (the unit only where the server gives one). */
export function windowWords(w: Pick<AcquisitionBrainWindow, "low" | "high" | "unit">): string {
  const unit = w.unit?.trim();
  return `${plainNumber(w.low)} to ${plainNumber(w.high)}${unit && !DASH_ONLY.test(unit) ? ` ${unit}` : ""}`;
}

export interface LoadWords {
  /** Rows an hour as the source is read today. */
  before: string;
  /** Rows an hour as the plan reads it. */
  after: string;
  /** How the plan changes it, as a percent of the rows read today; none where today's load is zero. */
  change?: { label: "Reduction" | "Increase" | "Change"; value: string };
}

const ROWS_HOUR = (n: number) => n.toLocaleString("en-US", { maximumFractionDigits: 1 });

/** The brain's load figures in words, with the reduction (or the increase) as a percent of today's rows an hour. */
export function loadWords(load: AcquisitionBrainLoad): LoadWords {
  const before = load.rows_per_hour_before;
  const after = load.rows_per_hour_after;
  const out: LoadWords = { before: ROWS_HOUR(before), after: ROWS_HOUR(after) };
  if (!(before > 0) || !Number.isFinite(after)) return out;
  const pct = ((before - after) / before) * 100;
  if (pct === 0) return { ...out, change: { label: "Change", value: "None" } };
  const abs = Math.abs(pct);
  // A whole percent; under one is said so, and a plan that still reads some rows is never said to read none.
  const whole = Math.round(abs);
  const value = whole < 1 ? "under 1%" : `${after > 0 && whole >= 100 && pct > 0 ? 99 : whole}%`;
  return { ...out, change: { label: pct > 0 ? "Reduction" : "Increase", value } };
}

/** The brain's questions as a plain numbered list, for the plant (the notes' Copy button), each with its points' names. */
export function brainQuestionsText(
  questions: readonly AcquisitionBrainNote[],
  pointName: (key: string) => string,
): string {
  if (questions.length === 0) return "";
  const lines = questions.map((q, i) => {
    const names = (q.points ?? []).map(pointName);
    return `${i + 1}. ${q.text}${names.length > 0 ? ` (${names.join(", ")})` : ""}`;
  });
  return ["Questions for the plant", ...lines].join("\n");
}
